import { createFileRoute } from "@tanstack/react-router";
import { getRouterInstance } from "@tanstack/react-start";
import {
  isSitemapRouteIncluded,
  sitemapPathForLocation,
  sitemapStaticPaths,
  sitemapXML,
  type SitemapEntry,
} from "@/lib/sitemap";

const BASE_URL = "https://youtubersmap.com";

async function publicSupabase() {
  const { createClient } = await import("@supabase/supabase-js");
  const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!;
  return createClient(process.env["SUPABASE_URL"]!, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const headers = new Headers(init?.headers);
        // Opaque sb_ keys are not JWTs; send apikey without the default bearer.
        if (key.startsWith("sb_") && headers.get("Authorization") === "Bearer " + key)
          headers.delete("Authorization");
        headers.set("apikey", key);
        return fetch(input, { ...init, headers });
      },
    },
  });
}

export const Route = createFileRoute("/sitemap.xml")({
  staticData: { sitemap: false },
  server: {
    handlers: {
      GET: async () => {
        const router = await getRouterInstance();
        const entries: SitemapEntry[] = sitemapStaticPaths(router).map((path) => ({ path }));
        const supabase = await publicSupabase();
        const pageSize = 1000;

        // /v/$slug — visible videos of visible youtubers
        const videoRouteId = "/v/$slug";
        if (isSitemapRouteIncluded(router.routesById[videoRouteId])) {
          for (let offset = 0; ; ) {
            const { data, error } = await supabase
              .from("videos")
              .select("id, slug, youtubers!inner(id)")
              .eq("hidden", false)
              .eq("deleted", false)
              .eq("youtubers.hidden", false)
              .order("id")
              .range(offset, offset + pageSize - 1);
            if (error) throw error;
            if (data.length === 0) break;
            for (const row of data) {
              const location = router.buildLocation({
                to: "/v/$slug",
                params: { slug: row.slug || row.id },
                search: () => ({}),
                hash: "",
              });
              const path = sitemapPathForLocation(router, location, videoRouteId);
              if (path) entries.push({ path });
            }
            offset += data.length;
          }
        }

        // /y/$handle — visible youtubers
        const youtuberRouteId = "/y/$handle";
        if (isSitemapRouteIncluded(router.routesById[youtuberRouteId])) {
          for (let offset = 0; ; ) {
            const { data, error } = await supabase
              .from("youtubers")
              .select("id, handle")
              .eq("hidden", false)
              .order("id")
              .range(offset, offset + pageSize - 1);
            if (error) throw error;
            if (data.length === 0) break;
            for (const row of data) {
              if (!row.handle) continue;
              const location = router.buildLocation({
                to: "/y/$handle",
                params: { handle: row.handle },
                search: () => ({}),
                hash: "",
              });
              const path = sitemapPathForLocation(router, location, youtuberRouteId);
              if (path) entries.push({ path });
            }
            offset += data.length;
          }
        }

        // /country/$code — countries with at least one visible video
        const countryRouteId = "/country/$code";
        if (isSitemapRouteIncluded(router.routesById[countryRouteId])) {
          const codes = new Set<string>();
          for (let offset = 0; ; ) {
            const { data, error } = await supabase
              .from("video_locations")
              .select("country_code, videos!inner(id, youtubers!inner(id))")
              .eq("videos.hidden", false)
              .eq("videos.deleted", false)
              .eq("videos.youtubers.hidden", false)
              .order("id")
              .range(offset, offset + pageSize - 1);
            if (error) throw error;
            if (data.length === 0) break;
            for (const row of data) {
              if (row.country_code) codes.add(row.country_code);
            }
            offset += data.length;
          }
          for (const code of [...codes].sort()) {
            const location = router.buildLocation({
              to: "/country/$code",
              params: { code },
              search: () => ({}),
              hash: "",
            });
            const path = sitemapPathForLocation(router, location, countryRouteId);
            if (path) entries.push({ path });
          }
        }

        if (entries.length === 0) {
          return new Response(
            'No pages are included in this sitemap. Check route decisions and ancestor exclusions. Setting "exclude-subtree" on the root excludes the entire site.',
            { status: 404, headers: { "Cache-Control": "no-store" } },
          );
        }
        return new Response(sitemapXML(BASE_URL, entries), {
          headers: { "Content-Type": "application/xml", "Cache-Control": "public, max-age=3600" },
        });
      },
    },
  },
});
